import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import AdminWebSource from './AdminWebSource';
import AdminProPlanSource from './AdminProPlanSource';

const s = {
  page: { display:'flex', minHeight:'calc(100vh - 56px)' },
  sidebar: { width:220, background:'var(--ink)', borderRight:'1px solid var(--ink-2)', flexShrink:0, padding:'24px 0' },
  sideItem: { display:'flex', alignItems:'center', gap:10, padding:'11px 20px', color:'#7a7567', fontSize:13, cursor:'pointer', transition:'all 0.15s', borderLeft:'2px solid transparent' },
  sideItemActive: { color:'var(--ink)', background:'var(--paper)', borderLeft:'2px solid var(--paper)' },
  sideLabel: { fontSize:10, color:'var(--muted-2)', letterSpacing:'2px', textTransform:'uppercase', padding:'16px 20px 8px', fontFamily:'var(--font-mono)' },
  main: { flex:1, padding:'32px 36px', background:'var(--paper)', overflowY:'auto' },
  pageTitle: { fontFamily:'var(--font-display)', fontSize:28, marginBottom:4, color:'var(--ink)' },
  pageSub: { color:'var(--muted)', fontSize:14, marginBottom:32 },
  statsGrid: { display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(180px, 1fr))', gap:16, marginBottom:32 },
  statCard: { background:'var(--card)', border:'1px solid var(--line-card)', borderRadius:0, padding:'20px 20px 16px' },
  statLabel: { fontSize:11, color:'var(--muted-2)', letterSpacing:'1.5px', textTransform:'uppercase', fontFamily:'var(--font-mono)', marginBottom:8 },
  statValue: { fontSize:28, fontWeight:700, fontFamily:'var(--font-mono)', lineHeight:1, color:'var(--ink)' },
  statChange: { fontSize:12, color:'var(--muted)', marginTop:6 },
  chartCard: { background:'var(--card)', border:'1px solid var(--line-card)', borderRadius:0, padding:'24px', marginBottom:24 },
  chartTitle: { fontSize:11, fontWeight:700, letterSpacing:'1.5px', textTransform:'uppercase', color:'var(--muted-2)', marginBottom:20, fontFamily:'var(--font-mono)' },
  table: { width:'100%', borderCollapse:'collapse' },
  th: { textAlign:'left', padding:'10px 14px', fontSize:11, fontWeight:700, letterSpacing:'1.5px', textTransform:'uppercase', color:'var(--muted-2)', borderBottom:'2px solid var(--line-card)', fontFamily:'var(--font-mono)', background:'var(--paper)' },
  td: { padding:'12px 14px', fontSize:13, borderBottom:'1px solid var(--line-card)', verticalAlign:'middle', color:'var(--ink)' },
  badge: { display:'inline-flex', alignItems:'center', padding:'2px 8px', borderRadius:0, fontSize:11, fontWeight:600, fontFamily:'var(--font-mono)' },
  btn: { background:'var(--ink)', color:'var(--paper)', border:'none', padding:'7px 14px', fontSize:12, fontWeight:600, borderRadius:0, cursor:'pointer' },
  outlineBtn: { background:'transparent', color:'var(--ink)', border:'1.5px solid var(--ink)', padding:'7px 14px', fontSize:12, borderRadius:0, cursor:'pointer' },
  dangerBtn: { background:'transparent', color:'var(--danger)', border:'1.5px solid var(--danger)', padding:'7px 14px', fontSize:12, borderRadius:0, cursor:'pointer' },
  searchInput: { padding:'9px 14px', border:'1px solid var(--line)', borderRadius:0, fontSize:13, outline:'none', width:240, fontFamily:'var(--font-body)' },
  filterRow: { display:'flex', gap:10, marginBottom:20, alignItems:'center', flexWrap:'wrap' },
  select: { padding:'8px 12px', border:'1px solid var(--line)', borderRadius:0, fontSize:13, outline:'none', fontFamily:'var(--font-body)' },
  pagination: { display:'flex', gap:6, justifyContent:'center', marginTop:20 },
  pageBtn: { width:34, height:34, border:'1px solid var(--line)', background:'var(--card)', borderRadius:0, fontSize:12, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'var(--font-mono)' },
  pageBtnActive: { background:'var(--ink)', color:'var(--paper)', border:'1px solid var(--ink)' },
  modal: { position:'fixed', inset:0, background:'rgba(10,10,10,0.7)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:24 },
  modalCard: { background:'var(--card)', border:'1px solid var(--line-card)', borderRadius:0, width:'100%', maxWidth:480, padding:32 },
  modalTitle: { fontSize:18, fontWeight:700, marginBottom:20, fontFamily:'var(--font-display)', color:'var(--ink)' },
  group: { marginBottom:16 },
  inputLabel: { display:'block', fontSize:12, fontWeight:600, letterSpacing:'1.5px', marginBottom:6, textTransform:'uppercase', color:'var(--muted-2)', fontFamily:'var(--font-mono)' },
  input: { width:'100%', padding:'10px 14px', border:'1px solid var(--line)', borderRadius:0, fontSize:14, outline:'none', fontFamily:'var(--font-body)' }
};

const ROLE_COLORS = { super_admin:'#0a0a0a', admin:'#1d4ed8', manager:'#7c3aed', editor:'#15803d', user:'#6b6b6b' };
const PLAN_COLORS = { annual:'#0a0a0a', monthly:'#1d4ed8', free:'#8a8479' };

const NavItem = ({ icon, label, id, active, onClick }) => (
  <div style={{ ...s.sideItem, ...(active ? s.sideItemActive : {}) }} onClick={() => onClick(id)}>
    <span style={{ fontSize:16 }}>{icon}</span><span>{label}</span>
  </div>
);

export default function AdminPanel() {
  const navigate = useNavigate();
  const [view, setView] = useState('dashboard');
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [newUser, setNewUser] = useState({ name:'', email:'', password:'', role:'user' });
  const [msg, setMsg] = useState('');
  const [traffic, setTraffic] = useState(null);

  useEffect(() => {
    if (view === 'dashboard') { fetchStats(); fetchTraffic(); }
    if (view === 'users') fetchUsers();
    if (view === 'subscribers') fetchSubscribers();
  }, [view, page, search, roleFilter, planFilter]); // eslint-disable-line

  const fetchStats = async () => {
    try { const { data } = await axios.get('/api/admin/stats'); setStats(data); } catch {}
  };

  const fetchTraffic = async () => {
    try { const { data } = await axios.get('/api/admin/analytics/traffic-summary'); if (data.success) setTraffic(data.data); } catch {}
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/admin/users', { params: { page, limit:20, search, role:roleFilter, plan:planFilter } });
      setUsers(data.users); setTotalUsers(data.total); setTotalPages(data.pages);
    } catch {}
    setLoading(false);
  };

  const fetchSubscribers = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/admin/subscribers', { params: { page, limit:20 } });
      setSubscribers(data.users); setTotalUsers(data.total); setTotalPages(data.pages);
    } catch {}
    setLoading(false);
  };

  const handleCreateUser = async () => {
    try {
      await axios.post('/api/admin/users', newUser);
      setCreateModal(false);
      setNewUser({ name:'', email:'', password:'', role:'user' });
      fetchUsers();
      setMsg('User created successfully');
    } catch (err) { setMsg(err.response?.data?.message || 'Failed'); }
  };

  const handleUpdateUser = async () => {
    try {
      await axios.put(`/api/admin/users/${editModal._id}`, editModal);
      setEditModal(null); fetchUsers();
    } catch {}
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('Delete this user permanently?')) return;
    try { await axios.delete(`/api/admin/users/${id}`); fetchUsers(); } catch {}
  };

  const toggleActive = async (user) => {
    try { await axios.put(`/api/admin/users/${user._id}`, { isActive: !user.isActive }); fetchUsers(); } catch {}
  };

  const registrationData = stats?.registrationTrend?.map(d => ({ date: d._id.slice(5), count: d.count })) || [];

  return (
    <div style={s.page}>
      {/* Mobile sidebar toggle */}
      <button
        className="admin-mobile-toggle"
        style={{ display: 'none', position: 'fixed', bottom: 20, right: 20, zIndex: 400, width: 48, height: 48, borderRadius: '50%', background: 'var(--ink)', color: 'var(--paper)', border: 'none', fontSize: 20, cursor: 'pointer', boxShadow: '0 4px 16px rgba(10,10,10,0.3)', alignItems: 'center', justifyContent: 'center' }}
        onClick={() => setMobileSidebarOpen(o => !o)}
      >
        {mobileSidebarOpen ? '×' : '☰'}
      </button>

      {/* Mobile sidebar overlay */}
      {mobileSidebarOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 350, display: 'flex' }} onClick={() => setMobileSidebarOpen(false)}>
          <div style={{ width: 220, background: 'var(--ink)', padding: '24px 0', overflowY: 'auto', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '0 20px 20px', borderBottom: '1px solid var(--ink-2)', marginBottom: 8 }}>
              <div style={{ color: 'var(--paper)', fontFamily: 'var(--font-display)', fontSize: 16 }}>Admin</div>
              <div style={{ color: 'var(--muted-2)', fontSize: 11, fontFamily: 'var(--font-mono)', marginTop: 2 }}>BioMedArticles Control</div>
            </div>
            <div style={s.sideLabel}>Navigation</div>
            <NavItem icon="◈" label="Dashboard" id="dashboard" active={view==='dashboard'} onClick={(id) => { setView(id); setMobileSidebarOpen(false); }} />
            <NavItem icon="⊞" label="Users" id="users" active={view==='users'} onClick={(id) => { setView(id); setMobileSidebarOpen(false); }} />
            <NavItem icon="◉" label="Subscribers" id="subscribers" active={view==='subscribers'} onClick={(id) => { setView(id); setMobileSidebarOpen(false); }} />
            <NavItem icon="◈" label="Analytics" id="analytics" active={view==='analytics'} onClick={(id) => { setView(id); setMobileSidebarOpen(false); }} />
            <div style={s.sideLabel}>Sources</div>
            <NavItem icon="◎" label="Web Source" id="web-source" active={view==='web-source'} onClick={(id) => { setView(id); setMobileSidebarOpen(false); }} />
            <NavItem icon="⬡" label="Pro Plan Source" id="proplan-source" active={view==='proplan-source'} onClick={(id) => { setView(id); setMobileSidebarOpen(false); }} />
            <div style={{ borderTop: '1px solid var(--ink-2)', marginTop: 12, paddingTop: 12 }}>
              <div onClick={() => { navigate('/admin/blog'); setMobileSidebarOpen(false); }} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', color: '#7a7567', fontSize: 13, fontFamily: 'var(--font-mono)' }}>
                <span>✍️</span> Blog Manager
              </div>
            </div>
          </div>
          <div style={{ flex: 1, background: 'rgba(10,10,10,0.5)' }} />
        </div>
      )}

      {/* Sidebar */}
      <div className="admin-sidebar" style={s.sidebar}>
        <div style={{ padding:'0 20px 20px', borderBottom:'1px solid var(--ink-2)', marginBottom:8 }}>
          <div style={{ color:'var(--paper)', fontFamily:'var(--font-display)', fontSize:16 }}>Admin</div>
          <div style={{ color:'var(--muted-2)', fontSize:11, fontFamily:'var(--font-mono)', marginTop:2 }}>BioMedArticles Control</div>
        </div>
        <div style={s.sideLabel}>Navigation</div>
        <NavItem icon="◈" label="Dashboard" id="dashboard" active={view==='dashboard'} onClick={setView} />
        <NavItem icon="⊞" label="Users" id="users" active={view==='users'} onClick={setView} />
        <NavItem icon="◉" label="Subscribers" id="subscribers" active={view==='subscribers'} onClick={setView} />
        <NavItem icon="◈" label="Analytics" id="analytics" active={view==='analytics'} onClick={setView} />
        <div style={s.sideLabel}>Sources</div>
        <NavItem icon="◎" label="Web Source" id="web-source" active={view==='web-source'} onClick={setView} />
        <NavItem icon="⬡" label="Pro Plan Source" id="proplan-source" active={view==='proplan-source'} onClick={setView} />
          <div style={{ borderTop: '1px solid var(--ink-2)', marginTop: 12, paddingTop: 12 }}>
            <div onClick={() => navigate('/admin/blog')} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', color: '#7a7567', textDecoration: 'none', fontSize: 13, borderRadius: 0, fontFamily: 'var(--font-mono)' }}
              onMouseEnter={e => { e.currentTarget.style.background='var(--ink-2)'; e.currentTarget.style.color='var(--paper)'; }}
              onMouseLeave={e => { e.currentTarget.style.background='transparent'; e.currentTarget.style.color='#7a7567'; }}>
              <span>✍️</span> Blog Manager
            </div>
          </div>
      </div>

      {/* Main Content */}
      <div className="admin-main" style={s.main}>
        {msg && <div style={{ background:'#f0fdf4', color:'#15803d', border:'1px solid #bbf7d0', padding:'10px 14px', borderRadius:0, marginBottom:16, fontSize:13 }}>{msg} <button onClick={() => setMsg('')} style={{ float:'right', background:'none', border:'none', cursor:'pointer' }}>×</button></div>}

        {/* Dashboard */}
        {view === 'dashboard' && stats && (
          <div>
            <h1 style={s.pageTitle}>Dashboard</h1>
            <p style={s.pageSub}>Platform overview — last 30 days</p>
            <div style={s.statsGrid}>
              {[
                { label:'Total Users', value: stats.totalUsers?.toLocaleString(), change:'All registered' },
                { label:'Pro Subscribers', value: stats.proUsers, change:'Active subscriptions' },
                { label:'Free Users', value: stats.freeUsers, change:'Free tier' },
                { label:'New (30d)', value: stats.recentRegistrations, change:'Last 30 days' },
                { label:'Active (7d)', value: stats.recentLogins, change:'Logged in this week' },
                { label:'Searches (30d)', value: stats.searchCount, change:'Search events' },
                { label:'Study Views', value: stats.studyViews, change:'Last 30 days' }
              ].map(item => (
                <div key={item.label} style={s.statCard}>
                  <div style={s.statLabel}>{item.label}</div>
                  <div style={s.statValue}>{item.value ?? '—'}</div>
                  <div style={s.statChange}>{item.change}</div>
                </div>
              ))}
            </div>

            {registrationData.length > 0 && (
              <div style={s.chartCard}>
                <div style={s.chartTitle}>New Registrations (30 days)</div>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={registrationData}>
                    <XAxis dataKey="date" tick={{ fontSize:11, fontFamily:'var(--font-mono)' }} />
                    <YAxis tick={{ fontSize:11, fontFamily:'var(--font-mono)' }} />
                    <Tooltip contentStyle={{ fontSize:12, fontFamily:'var(--font-mono)', border:'1px solid var(--line)' }} />
                    <Bar dataKey="count" fill="var(--ink)" radius={[0,0,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {stats.subscriptionBreakdown?.length > 0 && (
              <div style={s.chartCard}>
                <div style={s.chartTitle}>Subscription Breakdown</div>
                <div style={{ display:'flex', gap:24, alignItems:'center' }}>
                  <PieChart width={160} height={160}>
                    <Pie data={stats.subscriptionBreakdown} dataKey="count" nameKey="_id" cx="50%" cy="50%" outerRadius={70} innerRadius={40}>
                      {stats.subscriptionBreakdown.map((entry, i) => (
                        <Cell key={i} fill={PLAN_COLORS[entry._id] || '#ccc'} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize:12 }} />
                  </PieChart>
                  <div>
                    {stats.subscriptionBreakdown.map(entry => (
                      <div key={entry._id} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                        <div style={{ width:10, height:10, borderRadius:0, background: PLAN_COLORS[entry._id] || '#ccc' }} />
                        <span style={{ fontSize:13, fontFamily:'var(--font-mono)', textTransform:'uppercase', color:'var(--muted)' }}>{entry._id}</span>
                        <span style={{ fontSize:13, fontWeight:700, color:'var(--ink)' }}>{entry.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Traffic Overview Section */}
            {traffic && (
              <div style={{ marginTop: 8 }}>
                <div style={s.chartTitle}>Website Traffic</div>
                <div style={s.statsGrid}>
                  <div style={{ ...s.statCard, background: 'var(--ink)', border: '1px solid var(--ink)' }}>
                    <div style={{ ...s.statLabel, color: 'rgba(244,241,234,0.6)' }}>Today</div>
                    <div style={{ ...s.statValue, color: 'var(--paper)' }}>{traffic.todayVisitors?.toLocaleString() || 0}</div>
                    <div style={{ ...s.statChange, color: 'rgba(244,241,234,0.5)' }}>visitors</div>
                  </div>
                  <div style={s.statCard}>
                    <div style={s.statLabel}>This Week</div>
                    <div style={s.statValue}>{traffic.weekVisitors?.toLocaleString() || 0}</div>
                    <div style={s.statChange}>last 7 days</div>
                  </div>
                  <div style={s.statCard}>
                    <div style={s.statLabel}>This Month</div>
                    <div style={s.statValue}>{traffic.monthVisitors?.toLocaleString() || 0}</div>
                    <div style={s.statChange}>last 30 days</div>
                  </div>
                  {(traffic.topSources || []).slice(0, 3).map((src, i) => (
                    <div key={i} style={s.statCard}>
                      <div style={s.statLabel}>{src._id || 'Unknown'}</div>
                      <div style={s.statValue}>{src.count}</div>
                      <div style={s.statChange}>visitors (7d)</div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  <button style={{ ...s.statCard, cursor: 'pointer', border: '1px solid var(--ink)', background: 'var(--ink)', color: 'var(--paper)', padding: '10px 16px', fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-mono)' }} onClick={() => setView('web-source')}>
                    View Web Source →
                  </button>
                  <button style={{ ...s.statCard, cursor: 'pointer', padding: '10px 16px', fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-mono)' }} onClick={() => setView('proplan-source')}>
                    View Pro Plan Source →
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Users */}
        {view === 'users' && (
          <div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <div>
                <h1 style={s.pageTitle}>Users</h1>
                <p style={{ color:'var(--muted)', fontSize:14 }}>{totalUsers.toLocaleString()} total</p>
              </div>
              <button style={s.btn} onClick={() => setCreateModal(true)}>+ Create User</button>
            </div>
            <div style={s.filterRow}>
              <input style={s.searchInput} placeholder="Search name or email..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
              <select style={s.select} value={roleFilter} onChange={e => { setRoleFilter(e.target.value); setPage(1); }}>
                <option value="">All Roles</option>
                {['user','editor','manager','admin','super_admin'].map(r => <option key={r} value={r}>{r.replace('_',' ')}</option>)}
              </select>
              <select style={s.select} value={planFilter} onChange={e => { setPlanFilter(e.target.value); setPage(1); }}>
                <option value="">All Plans</option>
                {['free','monthly','annual'].map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div className="admin-table-wrap" style={{ background:'var(--card)', border:'1px solid var(--line-card)', borderRadius:0, overflow:'hidden' }}>
              <table style={s.table}>
                <thead><tr>
                  <th style={s.th}>User</th>
                  <th style={s.th}>Role</th>
                  <th style={s.th}>Plan</th>
                  <th style={s.th}>Status</th>
                  <th style={s.th}>Joined</th>
                  <th style={s.th}>Actions</th>
                </tr></thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={6} style={{ ...s.td, textAlign:'center', padding:32, color:'var(--muted)' }}>Loading...</td></tr>
                  ) : users.map(u => (
                    <tr key={u._id}>
                      <td style={s.td}>
                        <div style={{ fontWeight:600, fontSize:13, color:'var(--ink)' }}>{u.name}</div>
                        <div style={{ fontSize:12, color:'var(--muted)', fontFamily:'var(--font-mono)' }}>{u.email}</div>
                      </td>
                      <td style={s.td}><span style={{ ...s.badge, background: ROLE_COLORS[u.role] + '18', color: ROLE_COLORS[u.role] }}>{u.role?.replace('_',' ')}</span></td>
                      <td style={s.td}><span style={{ ...s.badge, background: PLAN_COLORS[u.subscription?.plan] + '18', color: PLAN_COLORS[u.subscription?.plan] || '#8a8479' }}>{u.subscription?.plan || 'free'}</span></td>
                      <td style={s.td}><span style={{ ...s.badge, background: u.isActive ? '#dcfce7' : '#fee2e2', color: u.isActive ? '#15803d' : '#b91c1c' }}>{u.isActive ? 'Active' : 'Inactive'}</span></td>
                      <td style={{ ...s.td, fontFamily:'var(--font-mono)', fontSize:12, color:'var(--muted)' }}>{new Date(u.createdAt).toLocaleDateString()}</td>
                      <td style={s.td}>
                        <div style={{ display:'flex', gap:6 }}>
                          <button style={s.outlineBtn} onClick={() => setEditModal({...u})}>Edit</button>
                          <button style={s.outlineBtn} onClick={() => toggleActive(u)}>{u.isActive ? 'Disable' : 'Enable'}</button>
                          <button style={s.dangerBtn} onClick={() => handleDeleteUser(u._id)}>×</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div style={s.pagination}>
                {Array.from({ length: Math.min(10, totalPages) }, (_, i) => i+1).map(p => (
                  <button key={p} style={{ ...s.pageBtn, ...(page === p ? s.pageBtnActive : {}) }} onClick={() => setPage(p)}>{p}</button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Subscribers */}
        {view === 'subscribers' && (
          <div>
            <h1 style={s.pageTitle}>Subscribers</h1>
            <p style={s.pageSub}>{totalUsers} active subscribers</p>
            <div className="admin-table-wrap" style={{ background:'var(--card)', border:'1px solid var(--line-card)', borderRadius:0, overflow:'hidden' }}>
              <table style={s.table}>
                <thead><tr>
                  <th style={s.th}>User</th>
                  <th style={s.th}>Plan</th>
                  <th style={s.th}>Status</th>
                  <th style={s.th}>Renewal</th>
                  <th style={s.th}>Cancels</th>
                </tr></thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={5} style={{ ...s.td, textAlign:'center', padding:32, color:'var(--muted)' }}>Loading...</td></tr>
                  ) : subscribers.map(u => (
                    <tr key={u._id}>
                      <td style={s.td}>
                        <div style={{ fontWeight:600, fontSize:13, color:'var(--ink)' }}>{u.name}</div>
                        <div style={{ fontSize:12, color:'var(--muted)', fontFamily:'var(--font-mono)' }}>{u.email}</div>
                      </td>
                      <td style={s.td}><span style={{ ...s.badge, background: PLAN_COLORS[u.subscription?.plan] + '18', color: PLAN_COLORS[u.subscription?.plan] || '#8a8479' }}>{u.subscription?.plan}</span></td>
                      <td style={s.td}><span style={{ ...s.badge, background: u.subscription?.status === 'active' ? '#dcfce7' : '#fef9c3', color: u.subscription?.status === 'active' ? '#15803d' : '#854d0e' }}>{u.subscription?.status}</span></td>
                      <td style={{ ...s.td, fontFamily:'var(--font-mono)', fontSize:12 }}>{u.subscription?.currentPeriodEnd ? new Date(u.subscription.currentPeriodEnd).toLocaleDateString() : '—'}</td>
                      <td style={{ ...s.td, fontSize:12 }}>{u.subscription?.cancelAtPeriodEnd ? <span style={{ color:'#b91c1c' }}>Yes</span> : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Analytics */}
        {view === 'analytics' && stats && (
          <div>
            <h1 style={s.pageTitle}>Analytics</h1>
            <p style={s.pageSub}>Platform usage metrics</p>
            <div style={s.statsGrid}>
              {[
                { label:'Searches (30d)', value: stats.searchCount },
                { label:'Study Views (30d)', value: stats.studyViews },
                { label:'Pro Users', value: stats.proUsers },
                { label:'Conversion Rate', value: stats.totalUsers ? `${((stats.proUsers/stats.totalUsers)*100).toFixed(1)}%` : '0%' }
              ].map(item => (
                <div key={item.label} style={s.statCard}>
                  <div style={s.statLabel}>{item.label}</div>
                  <div style={s.statValue}>{item.value ?? '—'}</div>
                </div>
              ))}
            </div>
            {registrationData.length > 0 && (
              <div style={s.chartCard}>
                <div style={s.chartTitle}>User Growth (30 days)</div>
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={registrationData}>
                    <XAxis dataKey="date" tick={{ fontSize:11 }} />
                    <YAxis tick={{ fontSize:11 }} />
                    <Tooltip contentStyle={{ fontSize:12, fontFamily:'var(--font-mono)', border:'1px solid var(--line)' }} />
                    <Line type="monotone" dataKey="count" stroke="var(--ink)" strokeWidth={2} dot={{ fill:'var(--ink)', r:3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}

        {/* Web Source */}
        {view === 'web-source' && <AdminWebSource />}

        {/* Pro Plan Source */}
        {view === 'proplan-source' && <AdminProPlanSource />}
      </div>

      {/* Create User Modal */}
      {createModal && (
        <div style={s.modal} onClick={e => e.target === e.currentTarget && setCreateModal(false)}>
          <div style={s.modalCard}>
            <h2 style={s.modalTitle}>Create User</h2>
            {[
              { key:'name', label:'Name', type:'text', placeholder:'Full name' },
              { key:'email', label:'Email', type:'email', placeholder:'email@domain.com' },
              { key:'password', label:'Password', type:'password', placeholder:'Password' }
            ].map(field => (
              <div key={field.key} style={s.group}>
                <label style={s.inputLabel}>{field.label}</label>
                <input style={s.input} type={field.type} placeholder={field.placeholder} value={newUser[field.key]} onChange={e => setNewUser({...newUser, [field.key]: e.target.value})} onFocus={e => e.target.style.borderColor='var(--ink)'} onBlur={e => e.target.style.borderColor='var(--line)'} />
              </div>
            ))}
            <div style={s.group}>
              <label style={s.inputLabel}>Role</label>
              <select style={{ ...s.input, cursor:'pointer' }} value={newUser.role} onChange={e => setNewUser({...newUser, role: e.target.value})}>
                {['user','editor','manager','admin','super_admin'].map(r => <option key={r} value={r}>{r.replace('_',' ')}</option>)}
              </select>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button style={s.btn} onClick={handleCreateUser}>Create User</button>
              <button style={s.outlineBtn} onClick={() => setCreateModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editModal && (
        <div style={s.modal} onClick={e => e.target === e.currentTarget && setEditModal(null)}>
          <div style={s.modalCard}>
            <h2 style={s.modalTitle}>Edit User</h2>
            <div style={s.group}>
              <label style={s.inputLabel}>Name</label>
              <input style={s.input} value={editModal.name} onChange={e => setEditModal({...editModal, name: e.target.value})} onFocus={e => e.target.style.borderColor='var(--ink)'} onBlur={e => e.target.style.borderColor='var(--line)'} />
            </div>
            <div style={s.group}>
              <label style={s.inputLabel}>Email</label>
              <input style={s.input} type="email" value={editModal.email} onChange={e => setEditModal({...editModal, email: e.target.value})} onFocus={e => e.target.style.borderColor='var(--ink)'} onBlur={e => e.target.style.borderColor='var(--line)'} />
            </div>
            <div style={s.group}>
              <label style={s.inputLabel}>Role</label>
              <select style={{ ...s.input, cursor:'pointer' }} value={editModal.role} onChange={e => setEditModal({...editModal, role: e.target.value})}>
                {['user','editor','manager','admin','super_admin'].map(r => <option key={r} value={r}>{r.replace('_',' ')}</option>)}
              </select>
            </div>
            <div style={s.group}>
              <label style={s.inputLabel}>Status</label>
              <select style={{ ...s.input, cursor:'pointer' }} value={editModal.isActive ? 'active' : 'inactive'} onChange={e => setEditModal({...editModal, isActive: e.target.value === 'active'})}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button style={s.btn} onClick={handleUpdateUser}>Save Changes</button>
              <button style={s.outlineBtn} onClick={() => setEditModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}